import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

/**
 * Office detail view (MAX-4065). Deliberately mirrors profile-refresh/styles.ts so the two
 * detail views stay visually identical, minus the left summary panel — the Figma gives
 * offices a full-width tab panel with the office name as the page title instead.
 */
export const styles = (theme: Theme) =>
	({
		// Fill the viewport so the sticky save bar stays pinned to the bottom on short tabs.
		root: {
			display: "flex",
			flexDirection: "column",
			minHeight: "calc(100vh - 220px)",
		},
		content: {
			flex: "1 0 auto",
		},
		backLink: {
			display: "inline-flex",
			alignItems: "center",
			gap: 4,
			padding: 0,
			marginTop: 8,
			marginBottom: theme.spacing(2),
			fontSize: 16,
			fontWeight: 600,
			lineHeight: "20px",
			color: theme.palette.custom.tertiaryAction,
			cursor: "pointer",
			"&:hover": {
				textDecoration: "none",
			},
		},
		title: {
			fontSize: 32,
			lineHeight: "40px",
			fontWeight: 700,
			color: theme.palette.custom.textPrimary,
			marginBottom: theme.spacing(2),
		},
		// Tab strip and the "View Office Members" link share one row, pushed to opposite ends.
		tabRow: {
			display: "flex",
			alignItems: "flex-end",
			justifyContent: "space-between",
			gap: theme.spacing(2),
			marginBottom: theme.spacing(3),
			[theme.breakpoints.down("sm")]: {
				flexDirection: "column",
				alignItems: "flex-start",
			},
		},
		tabBar: {
			display: "flex",
			[theme.breakpoints.down("sm")]: {
				overflowX: "auto",
				WebkitOverflowScrolling: "touch",
				width: "100%",
			},
		},
		tab: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: 6,
			padding: 0,
			flexShrink: 0,
			cursor: "pointer",
			fontFamily: theme.typography.fontFamily,
			fontSize: 16,
			fontWeight: 400,
			lineHeight: "24px",
			color: theme.palette.custom.textTertiary,
			background: "none",
			border: "none",
			outline: "none",
		},
		tabLabel: {
			padding: "0 16px",
		},
		tabActive: {
			color: theme.palette.custom.textPrimary,
		},
		tabUnderline: {
			width: "100%",
			height: 2,
			backgroundColor: theme.palette.custom.borderCard,
		},
		tabUnderlineActive: {
			backgroundColor: theme.palette.custom.primary,
		},
		viewMembersLink: {
			display: "inline-flex",
			alignItems: "center",
			gap: 4,
			color: theme.palette.custom.tertiaryAction,
			fontSize: 16,
			fontWeight: 600,
			lineHeight: "20px",
			cursor: "pointer",
			whiteSpace: "nowrap",
			"&:hover": {
				textDecoration: "none",
			},
		},
		section: {
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.custom.borderCard}`,
			borderRadius: 8,
			padding: theme.spacing(3),
			marginBottom: theme.spacing(2),
		},
		sectionTitle: {
			fontSize: 18,
			lineHeight: "24px",
			fontWeight: 700,
			color: theme.palette.custom.textPrimary,
			marginBottom: theme.spacing(2),
		},
		// Subsection headers (Image / Header / Description on the Website tab) are 16px.
		subTitle: {
			fontSize: 16,
			lineHeight: "24px",
			fontWeight: 700,
			color: theme.palette.custom.textPrimary,
			marginBottom: theme.spacing(1),
		},
		sectionDescription: {
			color: theme.palette.custom.textPrimary,
			marginBottom: theme.spacing(2),
		},
		formColumn: {
			width: "100%",
			boxSizing: "border-box",
			[theme.breakpoints.up("sm")]: {
				maxWidth: 520,
			},
			[theme.breakpoints.up("md")]: {
				maxWidth: 576,
			},
			[theme.breakpoints.up("lg")]: {
				maxWidth: 720,
			},
			[theme.breakpoints.up("xl")]: {
				maxWidth: 840,
			},
		},
		// Field rows that sit side by side (Phone/Fax, City/State).
		fieldRow: {
			display: "flex",
			gap: theme.spacing(2),
			"& > *": {
				flex: "1 1 0",
				minWidth: 0,
			},
			[theme.breakpoints.down("sm")]: {
				flexDirection: "column",
			},
		},
		fieldBlock: {
			marginBottom: theme.spacing(2),
		},
		// MLS ID row: the two inputs share the width, Remove sits after them.
		// Stretch rather than flex-start so the Remove cell spans the full row height and can
		// center its button against the inputs (the labels sit inside the fields cell).
		mlsRow: {
			display: "flex",
			alignItems: "stretch",
			gap: theme.spacing(2),
			marginBottom: theme.spacing(2),
		},
		mlsFields: {
			display: "flex",
			gap: theme.spacing(2),
			flex: "1 1 auto",
			minWidth: 0,
			"& > *": {
				flex: "1 1 0",
				minWidth: 0,
			},
			[theme.breakpoints.down("sm")]: {
				flexDirection: "column",
			},
		},
		// Wrapper that owns the vertical centering, so the button lines up with the inputs rather
		// than the labels above them. Centering against the label+input block would sit too high,
		// so the label's height is offset instead of guessed at with a fixed margin.
		removeCell: {
			display: "flex",
			alignItems: "center",
			// Cancels the label row (20px line-height + 8px margin) that the fields cell adds
			// above its inputs, leaving this cell centered on the inputs alone.
			marginTop: 28,
		},
		removeButton: {
			color: theme.palette.error.main,
			fontSize: 14,
			fontWeight: 600,
			textTransform: "none",
			whiteSpace: "nowrap",
			padding: 0,
			minWidth: 0,
			"&:hover": {
				background: "none",
				textDecoration: "underline",
			},
		},
		addButton: {
			color: theme.palette.custom.tertiaryAction,
			fontSize: 14,
			fontWeight: 600,
			textTransform: "none",
			padding: 0,
			minWidth: 0,
			"&:hover": {
				background: "none",
				textDecoration: "underline",
			},
		},
		radioGroup: {
			marginBottom: theme.spacing(1),
		},
		// Progressive disclosure under a selected radio, indented to line up past the radio glyph.
		radioDisclosure: {
			marginLeft: 30,
			marginBottom: theme.spacing(2),
		},
		uploadLink: {
			display: "inline-flex",
			alignItems: "center",
			gap: 6,
			color: theme.palette.custom.tertiaryAction,
			fontSize: 14,
			fontWeight: 600,
			textTransform: "none",
			padding: 0,
			minWidth: 0,
			"&:hover": {
				background: "none",
				textDecoration: "underline",
			},
		},
		// Dashed drop-zone framing the uploaded image, with the clear affordance top-right.
		imagePreview: {
			position: "relative",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			border: `1px dashed ${theme.palette.custom.borderCard}`,
			borderRadius: 4,
			padding: theme.spacing(3),
			minHeight: 180,
		},
		imagePreviewImg: {
			maxWidth: "100%",
			maxHeight: 220,
			display: "block",
		},
		imageClearButton: {
			position: "absolute",
			top: 8,
			right: 8,
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.custom.borderCard}`,
			width: 28,
			height: 28,
			"&:hover": {
				backgroundColor: theme.palette.background.paper,
			},
		},
		editorBlock: {
			marginBottom: theme.spacing(3),
		},
		characterCount: {
			display: "block",
			textAlign: "right",
			color: theme.palette.custom.textSecondary,
			fontSize: 12,
			marginTop: 4,
		},
		errorText: {
			color: theme.palette.error.main,
			fontSize: "0.75rem",
			fontWeight: "bold",
			marginTop: 4,
		},
	}) satisfies Record<string, CSSObject>;
